// 020 — Constantes Vitales / Balance Hídrico (SNS-MSP/HCU-form.020/2021,
// docs/msp/). Discreto puro: un Observation por medida, sin Questionnaire.
//   B. Constantes vitales (pulso, temperatura, FR, SpO2, PA) — LOINC, vital-signs.
//   C. Medidas antropométricas — vital-signs (LOINC donde está verificado).
//   D. Ingesta-eliminación / balance hídrico — CodeSystem local enfermeria-020.
//   E. Cuidados generales / F. Fecha de colocación de dispositivos — idem.
// Cada Observation lleva el turno AM/PM/HS y el día de enfermería elegidos
// (extensiones) y el responsable (performer). Los TOTAL y el balance no se
// guardan: se calculan (calcularBalanceHidrico) — acá para el registro actual
// y en la pestaña Signos Vitales por turno y por 24 h. Ver enfermeria.ts.
//
// Guardado en una sola transacción: si Medplum rechaza un dato no se guarda
// ninguno — reintentar no duplica nada (un mL duplicado falsea el balance).
import type { Observation } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react-hooks';
import type { JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import type { FormularioDiscretoProps, Par } from './index';
import { CampoDictado } from './CampoDictado';
import { ErrorGuardado } from './ErrorGuardado';
import { useGuardado } from './useGuardado';
import { LoteTransaccional } from './transaccionFhir';
import { EncabezadoMspCampos, valoresInicialesEncabezadoMsp } from '../EncabezadoMspCampos';
import { useDatosEncabezadoMsp } from '../useDatosEncabezadoMsp';
import { PA_DIASTOLICA_LOINC, PA_PANEL_LOINC, PA_SISTOLICA_LOINC, SIGNOS_VITALES, VITAL_SIGNS_CATEGORY } from './vitalSigns';
import {
  calcularBalanceHidrico,
  diaEnfermeriaDe,
  extensionesTurno,
  MEDIDAS_020,
  observacionDeMedida,
  registrosLiquidosDe,
  SISTEMA_ENFERMERIA_020,
  turnoDeHora,
  type DefMedida,
  type SeccionMedida,
  type Turno,
  type ValorMedida,
} from './enfermeria';
import { Responsable, SelectorDiaEnfermeria, SelectorTurno } from './SelectorTurno';
import { balance24hDelDia, datosCurvaTermica020Pdf, efectivoDeToma, extensionesDiasClinicos, horaTomaValida, primerDiaHoja, sumarDias, textoPresionArterial } from './constantes020Datos';
import { useResponsable } from './useResponsable';

// Peso y talla del formulario oficial van en C (antropometría), no en B.
const CONSTANTES_B = SIGNOS_VITALES.filter((s) => s.clave !== 'peso' && s.clave !== 'talla');
const SECCIONES: readonly SeccionMedida[] = ['C', 'D', 'E', 'F'];
const CLAVE_PA = 'pa';

/** Un Observation a crear + su par del PDF + la clave del estado del formulario que lo originó. */
interface Entrada {
  readonly observacion: Observation;
  readonly par: Par;
  readonly clave: string;
}

function CampoMedida({
  def,
  valor,
  especifique,
  onValor,
  onEspecifique,
}: {
  readonly def: DefMedida;
  readonly valor: ValorMedida | '';
  readonly especifique: string;
  readonly onValor: (v: ValorMedida | '') => void;
  readonly onEspecifique: (v: string) => void;
}): JSX.Element {
  const { t } = useTranslation();
  const id = `f020-${def.clave}`;
  const etiqueta = def.unidad ? `${def.etiqueta} (${def.unidad})` : def.etiqueta;
  if (def.tipo === 'booleano') {
    return (
      <div className="flex items-center gap-2">
        <Checkbox id={id} checked={valor === true} onCheckedChange={(v) => onValor(v === true)} />
        <Label htmlFor={id}>{etiqueta}</Label>
      </div>
    );
  }
  const numerico = def.tipo === 'ml' || def.tipo === 'cantidad' || def.tipo === 'entero';
  const campoEspecifique = def.especifique && (
    <CampoDictado
      id={`${id}-especifique`}
      etiqueta={`${def.etiqueta} — ${t('formularios.f020.especifique')}`}
      placeholder={t('formularios.f020.especifique')}
      valor={especifique}
      onChange={onEspecifique}
    />
  );
  if (def.tipo === 'texto') {
    // Los campos 'texto' siempre traen un valor de tipo string (o '') — nunca number/boolean.
    const valorTexto = typeof valor === 'string' ? valor : '';
    return (
      <div className="flex flex-col gap-1">
        <CampoDictado id={id} etiqueta={etiqueta} valor={valorTexto} onChange={onValor} />
        {campoEspecifique}
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-1">
      <Label htmlFor={id}>{etiqueta}</Label>
      <Input
        id={id}
        type={numerico ? 'number' : 'date'}
        min={numerico ? 0 : undefined}
        step={def.tipo === 'entero' ? 1 : undefined}
        value={typeof valor === 'boolean' ? '' : valor}
        onChange={(e) => onValor(e.target.value === '' ? '' : numerico ? Number(e.target.value) : e.target.value)}
      />
      {campoEspecifique}
    </div>
  );
}

function sinClaves<V>(registro: Record<string, V>, claves: ReadonlySet<string>, prefijo: string): Record<string, V> {
  return Object.fromEntries(Object.entries(registro).filter(([k]) => !claves.has(`${prefijo}${k}`)));
}

export function Formulario020({ subject, encounter, onGuardado }: FormularioDiscretoProps): JSX.Element {
  const { t } = useTranslation();
  const medplum = useMedplum();
  const responsable = useResponsable();
  const [camposEncabezado, setCamposEncabezado] = useState(() => valoresInicialesEncabezadoMsp());
  const datosEncabezadoMsp = useDatosEncabezadoMsp(subject, camposEncabezado, '020', t('hub.formulario.020'), {
    // Balance hídrico/constantes de enfermería: es un registro de internación; sin esto el encabezado diría "Consulta externa".
    servicio: 'Hospitalización',
  });
  const [turno, setTurno] = useState<Turno>(() => turnoDeHora(new Date().getHours()));
  const [dia, setDia] = useState(() => diaEnfermeriaDe(new Date()));
  const [constantes, setConstantes] = useState<Record<string, number | ''>>({});
  const [paSistolica, setPaSistolica] = useState<number | ''>('');
  const [paDiastolica, setPaDiastolica] = useState<number | ''>('');
  // Día de internación / día postquirúrgico (instructivo, bloque B) y hora de la toma (renglón HORA del papel).
  const [diaInternacion, setDiaInternacion] = useState<number | ''>('');
  const [diaPostquirurgico, setDiaPostquirurgico] = useState<number | ''>('');
  const [horaToma, setHoraToma] = useState('');
  const [medidas, setMedidas] = useState<Record<string, ValorMedida | ''>>({});
  const [especifiques, setEspecifiques] = useState<Record<string, string>>({});
  const { guardando, error, guardar } = useGuardado();

  const armarEntradas = (efectivo: string): Entrada[] => {
    const extensionesDias = extensionesDiasClinicos(diaInternacion, diaPostquirurgico);
    const ctx = { subject, encounter, turno, dia, efectivo, performer: responsable, extensiones: extensionesDias };
    const comunes = { extension: [...extensionesTurno(turno, dia), ...extensionesDias], performer: responsable ? [responsable] : undefined };
    const entradas: Entrada[] = [];

    for (const signo of CONSTANTES_B) {
      const valor = constantes[signo.clave];
      if (valor === '' || valor === undefined) continue;
      entradas.push({
        clave: `b:${signo.clave}`,
        par: { pregunta: `B · ${signo.etiqueta} (${signo.unidad})`, respuesta: String(valor) },
        observacion: {
          resourceType: 'Observation',
          status: 'final',
          category: [VITAL_SIGNS_CATEGORY],
          code: { coding: [{ system: 'http://loinc.org', code: signo.loinc, display: signo.etiqueta }] },
          subject,
          encounter,
          effectiveDateTime: efectivo,
          valueQuantity: { value: valor, unit: signo.unidad, system: 'http://unitsofmeasure.org', code: signo.unidadUcum },
          ...comunes,
        },
      });
    }
    // Sistólica y diastólica son 2 renglones del papel: una sola de las dos también se guarda.
    if (paSistolica !== '' || paDiastolica !== '') {
      const componentesPa = [
        paSistolica !== '' ? { code: { coding: [{ system: 'http://loinc.org', code: PA_SISTOLICA_LOINC, display: 'Sistólica' }] }, valueQuantity: { value: paSistolica, unit: 'mmHg', system: 'http://unitsofmeasure.org', code: 'mm[Hg]' } } : undefined,
        paDiastolica !== '' ? { code: { coding: [{ system: 'http://loinc.org', code: PA_DIASTOLICA_LOINC, display: 'Diastólica' }] }, valueQuantity: { value: paDiastolica, unit: 'mmHg', system: 'http://unitsofmeasure.org', code: 'mm[Hg]' } } : undefined,
      ].filter((c) => c !== undefined);
      entradas.push({
        clave: CLAVE_PA,
        par: { pregunta: `B · ${t('formularios.f020.paSistolica')} / ${t('formularios.f020.paDiastolica')}`, respuesta: textoPresionArterial(paSistolica, paDiastolica) },
        observacion: {
          resourceType: 'Observation',
          status: 'final',
          category: [VITAL_SIGNS_CATEGORY],
          code: { coding: [{ system: 'http://loinc.org', code: PA_PANEL_LOINC, display: 'Presión arterial' }] },
          subject,
          encounter,
          effectiveDateTime: efectivo,
          component: componentesPa,
          ...comunes,
        },
      });
    }

    for (const def of MEDIDAS_020) {
      const o = observacionDeMedida(def, medidas[def.clave], ctx, especifiques[def.clave]);
      if (!o) continue;
      const nota = o.note?.[0]?.text ? ` (${o.note[0].text})` : '';
      const valor = o.valueBoolean !== undefined ? t('formularios.f020.si') : `${o.valueQuantity?.value ?? o.valueInteger ?? o.valueDateTime ?? o.valueString ?? ''}`;
      entradas.push({
        clave: `m:${def.clave}`,
        par: { pregunta: `${def.seccion} · ${def.unidad ? `${def.etiqueta} (${def.unidad})` : def.etiqueta}`, respuesta: `${valor}${nota}` },
        observacion: o,
      });
    }
    return entradas;
  };

  const limpiar = (claves: ReadonlySet<string>): void => {
    setConstantes((prev) => sinClaves(prev, claves, 'b:'));
    setMedidas((prev) => sinClaves(prev, claves, 'm:'));
    setEspecifiques((prev) => sinClaves(prev, claves, 'm:'));
    if (claves.has(CLAVE_PA)) {
      setPaSistolica('');
      setPaDiastolica('');
    }
    // La hora de la toma es de ESTE registro; el día de internación / postquirúrgico se conservan (son del día, no de la toma).
    setHoraToma('');
  };

  // Totales de ESTE registro (sección D), mismo cálculo que el resumen de la pestaña.
  const totales = calcularBalanceHidrico(registrosLiquidosDe(armarEntradas(new Date().toISOString()).map((e) => e.observacion)))[0]?.total24h;

  const handleSubmit = (): Promise<void> => guardar(async () => {
    const efectivo = efectivoDeToma(dia, horaToma, new Date());
    if (efectivo === undefined) throw new Error(t('formularios.f020.horaInvalida'));
    const entradas = armarEntradas(efectivo);
    // Un valor que `observacionDeMedida` rechaza (negativo, decimal en un
    // entero, fecha incompleta) NO se guarda: sin este aviso se perdía en
    // silencio junto con el resto del formulario — en un balance hídrico eso
    // es un dato clínico desaparecido, no un campo cosmético.
    const guardables = new Set(entradas.map((e) => e.clave));
    const descartadas = MEDIDAS_020.filter((def) => {
      const v = medidas[def.clave];
      if (v === undefined || v === '' || v === false) return false;
      if (typeof v === 'string' && !v.trim()) return false;
      return !guardables.has(`m:${def.clave}`);
    });
    if (descartadas.length > 0) throw new Error(t('formularios.f020.valoresInvalidos', { campos: descartadas.map((d) => d.etiqueta).join(', ') }));
    if (entradas.length === 0) throw new Error(t('formularios.f020.sinDatos'));
    // Una sola transacción: todo el balance se guarda o nada (sin "guardado parcial" que el reintento duplique).
    const lote = new LoteTransaccional();
    for (const e of entradas) lote.crear<Observation>(e.observacion);
    await lote.enviar(medplum);
    const encabezado: Par[] = [
      { pregunta: t('enfermeria.turno'), respuesta: t(`enfermeria.turnos.${turno}`) },
      { pregunta: t('enfermeria.diaEnfermeria'), respuesta: dia },
      { pregunta: t('formularios.f020.horaToma'), respuesta: horaToma.trim() },
      { pregunta: t('formularios.f020.diaInternacion'), respuesta: diaInternacion === '' ? '' : String(diaInternacion) },
      { pregunta: t('formularios.f020.diaPostquirurgico'), respuesta: diaPostquirurgico === '' ? '' : String(diaPostquirurgico) },
      { pregunta: t('enfermeria.responsable'), respuesta: responsable?.display ?? t('enfermeria.sinResponsable') },
    ].filter((p) => p.respuesta.trim() !== '');
    // Ya está guardado: si estas búsquedas fallan el PDF sale sin curva ni balance de 24 h, pero no se reporta error (reintentar duplicaría las mediciones).
    // Ventana acotada por los dos lados (una hoja = 7 días de enfermería que terminan en `dia`; +2 días de margen superior por las horas 0-7 del HS):
    // sin el tope superior, revisar un día pasado con muchos registros más nuevos perdería datos por el `_count`.
    const consulta = (extra: readonly (readonly [string, string])[]): URLSearchParams => {
      const pares: [string, string][] = [
        ['patient', subject.reference ?? ''],
        ['status:not', 'entered-in-error'],
        ['date', `ge${sumarDias(primerDiaHoja(dia), -1)}`],
        ['date', `lt${sumarDias(dia, 2)}`],
        ['_sort', '-date'],
        ...extra.map(([k, v]): [string, string] => [k, v]),
      ];
      return new URLSearchParams(pares);
    };
    const [signosVitales, ingestaEliminacion] = await Promise.all([
      (medplum.searchResources('Observation', consulta([['code', 'http://loinc.org|8310-5,http://loinc.org|8867-4'], ['_count', '200']])) as Promise<Observation[]>).catch(() => undefined),
      (medplum.searchResources('Observation', consulta([['category', `${SISTEMA_ENFERMERIA_020}|ingesta-eliminacion`], ['_count', '500']])) as Promise<Observation[]>).catch(() => undefined),
    ]);
    const curvaTermica = signosVitales
      ? datosCurvaTermica020Pdf(signosVitales, dia, {
          titulo: t('formularios.f020.curva.titulo'),
          ejeX: t('formularios.f020.curva.ejeX'),
          ejeYTemperatura: t('formularios.f020.curva.ejeYTemperatura'),
          ejeYPulso: t('formularios.f020.curva.ejeYPulso'),
          nota: (rango) => t('formularios.f020.curva.nota', { rango }),
          etiquetaTemperatura: t('formularios.f020.curva.temperatura'),
          etiquetaPulso: t('formularios.f020.curva.pulso'),
          etiquetaNormal: t('formularios.f020.curva.normal'),
        })
      : undefined;
    // Balance del DÍA (24 h, los 3 turnos) con lo ya guardado; si no se pudo leer, el del registro actual (rotulado como tal).
    const totales24h = ingestaEliminacion ? balance24hDelDia(ingestaEliminacion, dia) : undefined;
    const paresTotales: Par[] = totales24h
      ? [
          { pregunta: `D · ${t('formularios.f020.total24hIngresos')}`, respuesta: String(totales24h.ingresos) },
          { pregunta: `D · ${t('formularios.f020.total24hEliminaciones')}`, respuesta: String(totales24h.eliminaciones) },
          { pregunta: `D · ${t('formularios.f020.balance24h')}`, respuesta: String(totales24h.balance) },
        ]
      : totales
        ? [
            { pregunta: `D · ${t('formularios.f020.totalIngresos')}`, respuesta: String(totales.ingresos) },
            { pregunta: `D · ${t('formularios.f020.totalEliminaciones')}`, respuesta: String(totales.eliminaciones) },
            { pregunta: `D · ${t('formularios.f020.balance')}`, respuesta: String(totales.balance) },
          ]
        : [];
    limpiar(new Set(entradas.map((e) => e.clave)));
    onGuardado([...encabezado, ...entradas.map((e) => e.par), ...paresTotales], datosEncabezadoMsp, undefined, undefined, undefined, undefined, undefined, undefined, undefined, curvaTermica);
  });

  return (
    <div className="flex flex-col gap-6">
      <EncabezadoMspCampos subject={subject} value={camposEncabezado} onChange={setCamposEncabezado} />

      <div className="flex flex-col gap-2">
        <div className="flex flex-wrap items-end gap-4">
          <SelectorTurno turno={turno} onChange={setTurno} />
          <SelectorDiaEnfermeria id="f020-dia" dia={dia} onChange={setDia} />
          <div className="flex flex-col gap-1">
            <Label htmlFor="f020-hora-toma">{t('formularios.f020.horaToma')}</Label>
            <Input id="f020-hora-toma" placeholder="hh:mm" className="w-28" value={horaToma} onChange={(e) => setHoraToma(e.target.value)} aria-invalid={horaToma.trim() !== '' && !horaTomaValida(horaToma)} />
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="f020-dia-internacion">{t('formularios.f020.diaInternacion')}</Label>
            <Input id="f020-dia-internacion" type="number" min={0} step={1} className="w-28" value={diaInternacion} onChange={(e) => setDiaInternacion(e.target.value === '' ? '' : Number(e.target.value))} />
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="f020-dia-postquirurgico">{t('formularios.f020.diaPostquirurgico')}</Label>
            <Input id="f020-dia-postquirurgico" type="number" min={0} step={1} className="w-28" value={diaPostquirurgico} onChange={(e) => setDiaPostquirurgico(e.target.value === '' ? '' : Number(e.target.value))} />
          </div>
        </div>
        <Responsable display={responsable?.display} />
      </div>

      <section className="flex flex-col gap-3">
        <h4 className="text-sm font-semibold">{t('formularios.f020.seccionB')}</h4>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          {CONSTANTES_B.map((signo) => (
            <div key={signo.clave} className="flex flex-col gap-1">
              <Label htmlFor={`f020-${signo.clave}`}>{`${signo.clave === 'fc' ? t('formularios.f020.pulso') : signo.etiqueta} (${signo.unidad})`}</Label>
              <Input
                id={`f020-${signo.clave}`}
                type="number"
                value={constantes[signo.clave] ?? ''}
                onChange={(e) => setConstantes((prev) => ({ ...prev, [signo.clave]: e.target.value === '' ? '' : Number(e.target.value) }))}
              />
            </div>
          ))}
          <div className="flex flex-col gap-1">
            <Label htmlFor="f020-pa-sistolica">{t('formularios.f020.paSistolica')}</Label>
            <Input id="f020-pa-sistolica" type="number" value={paSistolica} onChange={(e) => setPaSistolica(e.target.value === '' ? '' : Number(e.target.value))} />
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="f020-pa-diastolica">{t('formularios.f020.paDiastolica')}</Label>
            <Input id="f020-pa-diastolica" type="number" value={paDiastolica} onChange={(e) => setPaDiastolica(e.target.value === '' ? '' : Number(e.target.value))} />
          </div>
        </div>
      </section>

      {SECCIONES.map((seccion) => (
        <section key={seccion} className="flex flex-col gap-3">
          <h4 className="text-sm font-semibold">{t(`formularios.f020.seccion${seccion}`)}</h4>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            {MEDIDAS_020.filter((m) => m.seccion === seccion).map((def) => (
              <CampoMedida
                key={def.clave}
                def={def}
                valor={medidas[def.clave] ?? ''}
                especifique={especifiques[def.clave] ?? ''}
                onValor={(v) => setMedidas((prev) => ({ ...prev, [def.clave]: v }))}
                onEspecifique={(v) => setEspecifiques((prev) => ({ ...prev, [def.clave]: v }))}
              />
            ))}
          </div>
          {seccion === 'D' && totales && (
            <p className="text-sm text-muted-foreground" aria-live="polite">
              {t('formularios.f020.totalesDelRegistro')}: {t('formularios.f020.totalIngresos')} <strong>{totales.ingresos}</strong> · {t('formularios.f020.totalEliminaciones')} <strong>{totales.eliminaciones}</strong> · {t('formularios.f020.balance')} <strong>{totales.balance}</strong>
            </p>
          )}
        </section>
      ))}

      <ErrorGuardado error={error} />

      <Button type="button" className="w-fit" onClick={() => void handleSubmit()} disabled={guardando}>
        {t('formulario.guardar')}
      </Button>
    </div>
  );
}
